export type YouTubePlayerInstance = {
  playVideo: () => void
  pauseVideo: () => void
  seekTo: (seconds: number, allowSeekAhead: boolean) => void
  getCurrentTime: () => number
  getDuration: () => number
  getVideoLoadedFraction: () => number
  mute: () => void
  unMute: () => void
  isMuted: () => boolean
  getIframe: () => HTMLIFrameElement
  unloadModule: (module: string) => void
  destroy: () => void
}

type YouTubePlayerEvent = { target: YouTubePlayerInstance; data: number }

type YouTubePlayerOptions = {
  videoId: string
  width?: string
  height?: string
  playerVars?: Record<string, string | number>
  events?: {
    onReady?: (event: YouTubePlayerEvent) => void
    onStateChange?: (event: YouTubePlayerEvent) => void
    onError?: (event: YouTubePlayerEvent) => void
  }
}

type YouTubeNamespace = {
  Player: new (element: HTMLElement, options: YouTubePlayerOptions) => YouTubePlayerInstance
}

declare global {
  interface Window {
    YT?: YouTubeNamespace
    onYouTubeIframeAPIReady?: () => void
  }
}

export const YouTubeState = {
  unstarted: -1,
  ended: 0,
  playing: 1,
  paused: 2,
  buffering: 3,
  cued: 5,
} as const

let apiPromise: Promise<YouTubeNamespace> | null = null

export const loadYouTubeApi = async (): Promise<YouTubeNamespace> => {
  if (window.YT?.Player) return await Promise.resolve(window.YT)
  if (apiPromise) return await apiPromise

  apiPromise = new Promise((resolve, reject) => {
    const previousReady = window.onYouTubeIframeAPIReady
    window.onYouTubeIframeAPIReady = () => {
      previousReady?.()
      if (window.YT) resolve(window.YT)
    }

    const script = document.createElement('script')
    script.src = 'https://www.youtube.com/iframe_api'
    script.async = true
    script.onerror = () => {
      apiPromise = null
      script.remove()
      reject(new Error('YouTube IFrame API failed to load'))
    }
    document.head.appendChild(script)
  })

  return await apiPromise
}
